import React, { FC } from 'react';
import {font} from '@styles/fonts';
import { ColorFunction } from '@styles/colors/types';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import {ScaleFunction} from '@components/operatorPanel/interfaces';
import { scale } from '@components/frame';
import i18n from 'i18n-js';
import { convertTime, convertTimeLetter, formatTimeEvent, extractNumberFromString, extractLettersFromString, isAndroid } from '@helpers';

export interface TimeDisplayProps {
  C: ColorFunction;
  value: number | string;
  fontForDigit?: keyof typeof font;
  fontForLetter?: keyof typeof font;
  fontForSecondLetter?: keyof typeof font;
  fontForSecondDigit: keyof typeof font;
  fontForSecondDelimiter?: keyof typeof font;
  color?: string;
  withHMSFormat?: boolean;
  styles?: StyleProp<ViewStyle>;
  alreadyConverted?: boolean;
  fontForDelimiter?: keyof typeof font;
  colorAlreadyParsed?: boolean;
  includeSecondsAlways?: boolean;
  scale?: ScaleFunction;
  font?: typeof font;
  delimiterAlignCenter?: boolean;
}

const TimeDisplay: FC<TimeDisplayProps> = ({
  value,
  C,
  fontForLetter,
  fontForSecondLetter,
  fontForSecondDigit,
  withHMSFormat,
  styles,
  alreadyConverted,
  colorAlreadyParsed,
  fontForSecondDelimiter,
  font: fontProp = font,
  scale: scaleProp = scale,
  fontForDigit = 'Time 3',
  color = 'Label/Light/Primary',
  fontForDelimiter = 'Footnote',
  includeSecondsAlways = true,
  delimiterAlignCenter = true,
}) => {
  const valueIsNumber = typeof value === 'number';
  const valueIsString = typeof value === 'string';

  const parsedColor = colorAlreadyParsed ? color : C(color);

  // Android: Fabric's pixel-grid rounding can shave the text layout width under the measured width, wrapping the last digit onto a clipped extra line
  const digitClipGuard = isAndroid ? { paddingEnd: scaleProp(1) } : undefined;

  const gte100Hours = valueIsNumber ? value >= 100 * 60 * 60 : false;
  const gte1000Hours = valueIsNumber ? value >= 1000 * 60 * 60 : false;

  if (gte100Hours && valueIsNumber) {
    const formattedValue = gte1000Hours ? '+999' : Math.floor(value / 60 / 60);

    return (
      <View style={[{ flexDirection: 'row', alignItems: 'baseline' }, styles]}>
        <Text style={[fontProp[fontForDigit], { color: parsedColor }, digitClipGuard]}>{formattedValue}</Text>
        <Text style={[fontProp[fontForLetter ?? 'Footnote'], { color: parsedColor, includeFontPadding: true }]}>{i18n.t('h')}</Text>
      </View>
    );
  }

  if (withHMSFormat) {
    const formattedValue = alreadyConverted && valueIsString ? value : convertTime(value);
    const delimiter = ':';
    const timeParts = formattedValue.split(delimiter);

    return (
      <View style={[{ flexDirection: 'row', alignItems: 'baseline' }, styles]}>
        {timeParts.map((part, index) => {
          const lastPart = index === timeParts.length - 1;
          const isDelimiterBeforeSeconds = index === timeParts.length - 2;
          const digitFont = !lastPart ? fontForDigit : fontForSecondDigit;
          const delimiterFont = isDelimiterBeforeSeconds && fontForSecondDelimiter ? fontForSecondDelimiter : fontForDelimiter;

          return (
            <View key={index} style={{ flexDirection: 'row', alignItems: 'baseline' }}>
              <Text style={[fontProp[digitFont], { color: parsedColor }, lastPart && { fontVariant: ['tabular-nums'] }, digitClipGuard]}>{part}</Text>
              {delimiter && !lastPart && <Text style={[fontProp[delimiterFont], { color: parsedColor }, delimiterAlignCenter && { alignSelf: 'center' }]}>{delimiter}</Text>}
            </View>
          );
        })}
      </View>
    );
  }

  const formattedValue = alreadyConverted && valueIsString ? value : formatTimeEvent(convertTimeLetter(value), false, includeSecondsAlways);

  const timeParts = formattedValue ? formattedValue.split(' ').filter(Boolean) : [`0${i18n.t('s')}`];

  return (
    <View style={[{ flexDirection: 'row', alignItems: 'baseline', gap: scaleProp(4) }, styles]}>
      {timeParts.map((part, index) => {
        const number = extractNumberFromString(part);
        const letter = extractLettersFromString(part);

        const letterFont = letter === i18n.t('s') ? fontForSecondLetter : fontForLetter;
        const digitFont = letter === i18n.t('s') ? fontForSecondDigit : fontForDigit;

        return (
          <View key={index} style={{ flexDirection: 'row', alignItems: 'baseline' }}>
            <Text style={[fontProp[digitFont], { color: parsedColor, fontVariant: ['tabular-nums'], includeFontPadding: true }, digitClipGuard]}>{number}</Text>
            {letter && <Text style={[fontProp[letterFont ?? 'Footnote'], { color: parsedColor, includeFontPadding: true }]}>{i18n.t(letter)}</Text>}
          </View>
        );
      })}
    </View>
  );
};

export default TimeDisplay;